const {
  getCookie,
  setCookie,
  interceptNativeSubmit,
  subscribeToNewsletter,
  wasFormPosted,
} = window.BAO.Utils

class PushCartNewsletter extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form', 'email', 'error', 'success']
  }

  constructor () {
    super()

    this.signupSource = 'website_cart'
  }

  connectedCallback () {
    super.connectedCallback()

    this.checkVisibility()

    // Back from a native or `/challenge` submission that Shopify accepted.
    if (wasFormPosted(this.els.form.element)) {
      this.revealPostedSuccess()
    }
  }

  // The form sits in the closed cart drawer, so open it before applying the discount.
  async revealPostedSuccess () {
    await Promise.all(
      ['site-drawers', 'site-drawer'].map(name =>
        customElements.whenDefined(name),
      ),
    )

    this.externalEls['cart-trigger']?.element?.click()

    this.showSuccess()
  }

  async checkVisibility () {
    if (this.isSubscribed === 'true') {
      // User is already subscribed
      this.ariaHidden = true
    }
    // To-do: Run Ometria account lookup
  }

  setupListeners () {
    super.setupListeners()

    this.submitForm = interceptNativeSubmit(
      this.els.form.element,
      () =>
        this.createClientSubscription({
          emailAddress: this.els.email.element.value.replace(/\s/g, ''),
        }),
      { source: this.signupSource },
    )

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.onFormSubmit.bind(this),
    )

    this.listeners.add(
      this.els.email.element,
      'input',
      this.handleEmailInput.bind(this),
    )
  }

  handleEmailInput () {
    if (this.validEmail()) {
      this.els.email.element.setAttribute('aria-invalid', false)
    } else {
      this.els.email.element.setAttribute('aria-invalid', true)
    }
  }

  async onFormSubmit (e) {
    e.preventDefault()

    // Only show the outcome of the latest submission.
    this.toggleErrorMessage()
    this.els.success.element.setAttribute('aria-hidden', true)

    if (!this.validEmail()) {
      // Stop Shopify's own submit listeners from sending an invalid form.
      e.stopImmediatePropagation()
      this.els.email.element.setAttribute('aria-invalid', true)
      this.els.email.element.focus()
      return
    }

    // To-do: Run Ometria account lookup

    this.submitForm()
  }

  async createClientSubscription (formData) {
    const { emailAddress } = formData

    const subscribed = await subscribeToNewsletter(emailAddress, {
      source: this.signupSource,
      form: this.els.form.element,
    })

    if (!subscribed) {
      this.toggleErrorMessage(window.theme.strings.newsletterGenericError)
      return
    }

    this.toggleErrorMessage()
    this.showSuccess()
  }

  toggleErrorMessage (message = false) {
    const errorElement = this.els.error.element
    if (!message) {
      errorElement.setAttribute('aria-hidden', true)
    } else {
      errorElement.setAttribute('aria-hidden', false)
      errorElement.innerHTML = message
    }
  }

  async showSuccess () {
    const trigger = this.els?.['trigger-text']?.element
    const success = this.els?.success?.element
    const submit = this.els?.submit?.element

    if (submit) {
      submit.remove()
    }

    if (success) {
      success.removeAttribute('aria-hidden')
    }

    if (trigger && success) {
      trigger.innerText = success.innerText
    }

    // Re-rendering this section would replace the success message with a fresh form.
    await window.BAO.Cart.updateCartDiscount(this.dataset.discountCode, {
      sections: window.BAO.sectionRenderer.pushCartSections
        .map(s => s.section)
        .filter(section => section !== 'push-cart-newsletter'),
    })

    setCookie(this.subscribedCustomerCookieName, 'true', 7)

    trigger.click()

    setTimeout(() => {
      this.ariaHidden = true
    }, 3000)
  }

  validEmail () {
    const emailAddress = this?.els?.email?.element?.value.replace(/\s/g, '')

    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
    return emailRegex.test(emailAddress)
  }

  get isSubscribed () {
    return getCookie(this.subscribedCustomerCookieName)
  }

  get subscribedCustomerCookieName () {
    return `BAO-subscribedCustomer`
  }
}

if (!customElements.get('push-cart-newsletter')) {
  customElements.define('push-cart-newsletter', PushCartNewsletter)
}
